import {
  ArrowRightIcon,
  LightBulbIcon,
  StarIcon,
} from "@heroicons/react/16/solid";
import {
  AnswerDTO,
  FreeTextAnswerSubmissionDTO,
  FreeTextQuestionEvaluationDTO,
  MultipleChoiceQuestionEvaluationDTO,
  MultipleChoiceQuestionSubmissionDTO,
  QuestionDTO,
  QuestionEvaluationDTOType,
  QuestionSubmissionDTOType,
} from "@onetutor/shared/dto";
import { QuestionType } from "@onetutor/shared/type";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useToastEffect } from "../hooks";
import {
  NO_QUESTIONS_LEFT_MODAL_ID,
  QuestionsStatus,
} from "../pages/quizpage/QuizSessionPage";
import { useGetTipQuery, useSubmitQuestionMutation } from "../services/quizApi";
import { MarkdownContent } from "./Markdown";
import FeedbackThumbs from "./feedbackThumbs";
import Modal, { openModal } from "./modal";

interface QuizCardProps {
  question: QuestionDTO;
  questionsLength: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  didIncreaseIndex: boolean;
  setDidIncreaseIndex: React.Dispatch<React.SetStateAction<boolean>>;
  isChecked: boolean;
  setCheckedQuestions: React.Dispatch<React.SetStateAction<QuestionsStatus[]>>;
  evaluation: QuestionEvaluationDTOType | null;
  currentIndex: number;
  allQuestionsSubmitted: boolean;
}

interface QuizCardWrapperProps {
  quizData: QuizCardProps;
}

export const QuizCard = ({ quizData }: QuizCardWrapperProps) => {
  const {
    question,
    questionsLength,
    setCurrentIndex,
    didIncreaseIndex,
    setDidIncreaseIndex,
    isChecked,
    setCheckedQuestions,
    evaluation,
    currentIndex,
    allQuestionsSubmitted,
  } = quizData;

  const { t } = useTranslation("quizPage");

  const [isShaking, setIsShaking] = useState(false);

  const [tip, setTip] = useState<string | null>(null);

  const tipModalId = "tip_modal";
  const [isFetchTip, setIsFetchTip] = useState(false);

  const [clickedSubmit, setClickedSubmit] = useState(false);

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("Course ID is required");
  }

  const { data: dataTip, isLoading: isLoadingTip } = useGetTipQuery(
    { courseId, questionId: question.id },
    { skip: !isFetchTip },
  );

  const slideClass = didIncreaseIndex
    ? "animate-slide-in-from-bottom"
    : "animate-slide-in-from-top";

  useEffect(() => {
    setIsFetchTip(false);
    setClickedSubmit(false);
    setTip(null);
    setIsShaking(false);

    setQuizHandling(initializeQuizHandling(question));
  }, [question]);

  const [
    submitQuestion,
    { isLoading: isSubmitting, error: errorSubmit, isError: isErrorSubmit },
  ] = useSubmitQuestionMutation();

  useToastEffect({ error: errorSubmit, isError: isErrorSubmit });

  const initializeQuizHandling = (
    question: QuestionDTO,
  ): QuestionSubmissionDTOType => {
    if (question.type === QuestionType.MULTIPLE_CHOICE) {
      return {
        type: question.type,
        answerIds: [],
      };
    } else {
      return {
        type: QuestionType.FREE_TEXT,
        answer: "",
      };
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO fix dependencies
  useEffect(() => {
    if (dataTip) {
      setTip(dataTip);
    } else if (!dataTip && isFetchTip) {
      openModal(tipModalId);
    }
  }, [dataTip]);

  const [quizHandling, setQuizHandling] = useState<QuestionSubmissionDTOType>(
    initializeQuizHandling(question),
  );

  const handleMultipleChoiceAnswerSelect = (answerId: string) => {
    if (quizHandling.type === QuestionType.MULTIPLE_CHOICE) {
      let currentAnswerIds = quizHandling.answerIds;

      if (currentAnswerIds.includes(answerId)) {
        currentAnswerIds = currentAnswerIds.filter((id) => id !== answerId);
      } else {
        currentAnswerIds = [...currentAnswerIds, answerId];
      }

      setQuizHandling({
        ...quizHandling,
        answerIds: currentAnswerIds,
      } as MultipleChoiceQuestionSubmissionDTO);
    }
  };

  const goToNextQuestion = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex + 1 < questionsLength) {
        setDidIncreaseIndex(true);
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const isCorrectMCSubmission = (
    evalData: MultipleChoiceQuestionEvaluationDTO,
  ): boolean => {
    const submitted = evalData.selectedAnswerIds;
    const correct = evalData.correctAnswerIds;

    return (
      submitted.length === correct.length &&
      correct.every((id) => submitted.includes(id))
    );
  };

  const handleSubmit = async () => {
    setClickedSubmit(true);

    const evalData = await submitQuestion({
      courseId,
      questionId: question.id,
      submission: quizHandling,
    }).unwrap();

    if (evalData.type === QuestionType.MULTIPLE_CHOICE) {
      if (!isCorrectMCSubmission(evalData)) {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    }

    setCheckedQuestions((prevCheckedQuestions) => {
      return prevCheckedQuestions.map((q) =>
        q.questionId === question.id
          ? { ...q, isChecked: true, evaluation: evalData }
          : q,
      );
    });
  };

  return (
    <div
      key={question.id}
      className={`card flex h-auto w-full max-w-[850px] overflow-y-auto bg-base-200 p-2 text-center shadow-sm sm:w-3/4 ${slideClass}`}
    >
      <div className="mx-10 p-4 text-left font-bold">
        <MarkdownContent>{question.question}</MarkdownContent>
      </div>
      {tip ? (
        isLoadingTip ? (
          <span className="loading loading-spinner loading-md" />
        ) : (
          <div className="mb-4 ml-lg flex items-center justify-start px-2 text-sm italic md:px-10">
            <LightBulbIcon className="mr-sm h-5 w-5 flex-shrink-0" />
            <MarkdownContent>{tip}</MarkdownContent>
          </div>
        )
      ) : (
        <div />
      )}
      <button
        className="btn btn-primary btn-sm absolute top-3 right-3"
        onClick={() => setIsFetchTip(true)}
        type="button"
      >
        ?
      </button>
      <div className="absolute bottom-3 left-3">
        <FeedbackThumbs
          feedbackType="question"
          courseId={courseId}
          id={question.id}
        />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div
          className={`flex h-full flex-col items-center gap-2 ${isShaking ? "animate-shake" : ""}`}
        >
          {question.type === QuestionType.FREE_TEXT ? (
            <>
              <textarea
                disabled={isChecked || clickedSubmit}
                required
                maxLength={5000}
                className="textarea h-full max-h-60 min-h-32 w-4/5 resize-none rounded-lg pt-sm disabled:bg-base-100 disabled:text-neutral-content"
                placeholder={t("textInputPlaceholder")}
                value={
                  (quizHandling as FreeTextAnswerSubmissionDTO).answer || ""
                }
                onInput={(e) => {
                  e.currentTarget.style.height = "auto";
                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                }}
                onChange={(e) =>
                  setQuizHandling({
                    ...quizHandling,
                    answer: e.target.value,
                  } as FreeTextAnswerSubmissionDTO)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {isChecked && evaluation && (
                <>
                  <div className="my-1 w-4/5 text-sm">
                    <MarkdownContent>
                      {(evaluation as FreeTextQuestionEvaluationDTO).reasoning}
                    </MarkdownContent>
                  </div>
                  <div className="flex items-center justify-center gap-2 font-bold">
                    {t("score")}:{" "}
                    {(evaluation as FreeTextQuestionEvaluationDTO).score} / 5
                    <StarIcon className="h-5 w-5" />
                  </div>
                </>
              )}
            </>
          ) : (
            (quizHandling as MultipleChoiceQuestionSubmissionDTO).answerIds &&
            question.answers.map((answer: AnswerDTO) => {
              const isCorrect = evaluation
                ? (
                    evaluation as MultipleChoiceQuestionEvaluationDTO
                  ).correctAnswerIds.includes(answer.id)
                : false;
              const isSelected = evaluation
                ? (
                    evaluation as MultipleChoiceQuestionEvaluationDTO
                  ).selectedAnswerIds.includes(answer.id)
                : (
                    quizHandling as MultipleChoiceQuestionSubmissionDTO
                  ).answerIds.includes(answer.id);
              return (
                <button
                  key={answer.id}
                  className={`btn btn-lg h-auto w-4/5 py-3 text-sm sm:py-5 sm:text-md ${isSelected ? "btn-primary" : "btn-neutral"}
    ${isChecked ? `pointer-events-none ${isCorrect ? "border-4 border-success border-solid" : "opacity-50"}` : ""}
  `}
                  type="button"
                  onClick={() => {
                    if (!isChecked) {
                      handleMultipleChoiceAnswerSelect(answer.id);
                    }
                  }}
                >
                  <MarkdownContent>{answer.answer}</MarkdownContent>
                </button>
              );
            })
          )}
          <div className="flex w-full justify-end">
            {!isChecked ? (
              <button
                type="submit"
                disabled={isChecked}
                className="btn-primary btn btn-circle mr-2 mb-1 text-white"
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-md" />
                ) : (
                  <ArrowRightIcon className="h-6 w-6" />
                )}
              </button>
            ) : currentIndex + 1 === questionsLength &&
              allQuestionsSubmitted ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => openModal(NO_QUESTIONS_LEFT_MODAL_ID)}
              >
                {t("restartQuiz")}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => goToNextQuestion()}
              >
                {t("nextQuestion")}
              </button>
            )}
          </div>
        </div>
      </form>
      <Modal id={tipModalId} title={t("tipModalName")} content={t("noTip")} />
    </div>
  );
};

export default QuizCard;
